/**
 * Start/finish gate pane — compact strip that sits ABOVE the map
 * (coordinates used to live at the bottom of the page, below the tall
 * map card, so placing gates from pasted coordinates meant scrolling
 * back and forth).
 *
 * One combined "lat, lon" field per gate — paste anything: a Google
 * Maps pin copy ("58.38753, 26.73208"), space/semicolon pairs, N/S/E/W
 * suffixes, or a maps URL; parseLatLon() normalizes it. Coordinate
 * inputs remain the source of truth; the map picker mirrors them.
 */

import React, { useState } from 'react';
import { PlayCircle, StopCircle } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GatePos } from '@/components/GateMapPicker';
import { cn } from '@/lib/utils';

import { formatLatLon, parseLatLon } from './latlon';
import { RADIUS_MAX, RADIUS_MIN, clamp } from './stageModel';

export const GatePane: React.FC<{
  kind: 'start' | 'finish';
  value: GatePos;
  onChange: (next: GatePos) => void;
}> = ({ kind, value, onChange }) => {
  const isStart = kind === 'start';
  const accentClass = isStart ? 'text-status-online' : 'text-status-error';
  const borderClass = isStart ? 'border-status-online/40' : 'border-status-error/40';
  const Icon = isStart ? PlayCircle : StopCircle;
  const title = isStart ? 'Start gate' : 'Finish gate';

  // Draft holds raw text while the user types/pastes; null = mirror the
  // committed value (so map click-to-place shows through when idle).
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? formatLatLon(value.lat, value.lon);
  const invalid = draft != null && draft.trim() !== '' && parseLatLon(draft) == null;

  const handleText = (raw: string) => {
    setDraft(raw);
    if (raw.trim() === '') {
      onChange({ ...value, lat: null, lon: null });
      return;
    }
    const parsed = parseLatLon(raw);
    if (parsed) onChange({ ...value, lat: parsed.lat, lon: parsed.lon });
  };

  // Radius uses the same draft pattern as the coordinates: clamping on
  // every keystroke into a controlled input rewrote what the user was
  // typing (typing "1" toward "100" clamped to 5, so the next
  // keystrokes made 50/500). While typing, only already-in-range values
  // commit (live map-circle feedback); the clamp happens once, on blur.
  const [radiusDraft, setRadiusDraft] = useState<string | null>(null);
  const radiusText = radiusDraft ?? String(value.radius_m);

  const handleRadiusText = (raw: string) => {
    setRadiusDraft(raw);
    const n = Number(raw);
    if (raw.trim() !== '' && Number.isFinite(n) && n >= RADIUS_MIN && n <= RADIUS_MAX) {
      onChange({ ...value, radius_m: Math.round(n) });
    }
  };

  const commitRadius = () => {
    const n = Number(radiusDraft ?? '');
    if (radiusDraft != null && radiusDraft.trim() !== '' && Number.isFinite(n)) {
      onChange({ ...value, radius_m: clamp(Math.round(n), RADIUS_MIN, RADIUS_MAX) });
    }
    setRadiusDraft(null); // empty/garbage reverts to the committed value
  };

  return (
    <Card className={`border ${borderClass} p-3`}>
      <CardContent className="space-y-2 p-0">
        <div className="flex items-center gap-2">
          <Icon className={`size-4 ${accentClass}`} />
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${accentClass}`}>
            {title}
          </h3>
          <div className="ml-auto flex items-center gap-1.5">
            <Input
              type="number"
              min={RADIUS_MIN}
              max={RADIUS_MAX}
              step={5}
              value={radiusText}
              onChange={(e) => handleRadiusText(e.target.value)}
              onBlur={commitRadius}
              className="h-7 w-[76px] font-mono text-xs"
              aria-label={`${title} radius in metres`}
              title={`${RADIUS_MIN}–${RADIUS_MAX} m · field default 50 m`}
            />
            <span className="text-[11px] text-fg-muted">m</span>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-fg-secondary">
            Coordinates <span className="text-fg-muted">— paste “lat, lon”</span>
          </Label>
          <Input
            value={text}
            onChange={(e) => handleText(e.target.value)}
            onBlur={() => setDraft(null)}
            placeholder="58.38753, 26.73208"
            className={cn('font-mono text-xs', invalid && 'border-status-error')}
            inputMode="decimal"
            spellCheck={false}
            aria-invalid={invalid || undefined}
          />
          {invalid && (
            <p className="text-[11px] text-status-error">
              Can’t read that — expected “lat, lon” in decimal degrees.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GatePane;
